/**
 * CampaignsWidget — analytics-v2 campaigns (ADS-054 / CORE-959).
 *
 * Fetches from GET /analytics-v2/campaigns with the currently-selected window
 * and renders a sortable per-campaign table (Spend, Impressions, Clicks,
 * Landings, Orders, Revenue, ROAS, CAC). Rows arrive from the backend sorted
 * by spend descending; local sort state is layered on top so operators can
 * re-rank by any numeric column without a refetch.
 *
 * All shell concerns (PROD badge, window selector, attribution caption,
 * loading/error triad, exhaustive source-degradation banner, ErrorBoundary)
 * live in AnalyticsV2Widget. This file owns only the widget-specific body
 * (the sortable table + empty state).
 *
 * Null handling is load-bearing (see AnalyticsCampaignRow docs):
 *   - `landings === null`  → "—" (GA4 unavailable; NOT rendered as "0").
 *   - `roas === null`      → "—" (spend=0; distinct from "spent → zero return").
 *   - `cac === null`       → "—" (newBuyers=0; distinct from real 0.00).
 */

import { useMemo, useState } from 'react';
import { getAnalyticsCampaigns } from '@/api/client';
import { formatCount, formatCurrency } from '@/utils/format';
import { compareNullable, type SortDir } from '@/utils/sort';
import { degradedSourceLabels } from '@/utils/sourceStatuses';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { useSortState } from '@/hooks/useSortState';
import { AnalyticsV2Widget } from './AnalyticsV2Widget';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type { AnalyticsCampaignRow, FunnelWindow } from '@/types/api';

const DESCRIPTION = (
  <>
    <p>
      Meta ad campaign performance for the selected window: spend,
      impressions, clicks, landings on the site, orders attributed, revenue,
      ROAS (revenue / spend), and CAC (spend / orders).
    </p>
    <p>
      Empty when no campaigns are running. Click any numeric column header
      to sort.
    </p>
  </>
);

type SortKey = 'spend' | 'impressions' | 'clicks' | 'landings' | 'orders' | 'revenue' | 'roas' | 'cac';

interface Column {
  key: SortKey;
  label: string;
  format: (row: AnalyticsCampaignRow) => string;
  getValue: (row: AnalyticsCampaignRow) => number | null;
}

// Column table — declaration order defines display order. Each column supplies
// both a formatter (formatCount dashes nulls) and a raw value extractor for
// sorting. formatCurrency (Intl-based) is preferred over hand-rolled `$X.XX`
// so negative amounts render as `-$X.XX` not `$-X.XX`.
const COLUMNS: readonly Column[] = [
  { key: 'spend', label: 'Spend', format: (r) => formatCurrency(r.spend, 'USD'), getValue: (r) => r.spend },
  { key: 'impressions', label: 'Impressions', format: (r) => formatCount(r.impressions), getValue: (r) => r.impressions },
  { key: 'clicks', label: 'Clicks', format: (r) => formatCount(r.clicks), getValue: (r) => r.clicks },
  { key: 'landings', label: 'Landings', format: (r) => formatCount(r.landings), getValue: (r) => r.landings },
  { key: 'orders', label: 'Orders', format: (r) => formatCount(r.orders), getValue: (r) => r.orders },
  { key: 'revenue', label: 'Revenue', format: (r) => formatCurrency(r.revenue, 'USD'), getValue: (r) => r.revenue },
  { key: 'roas', label: 'ROAS', format: (r) => r.roas === null ? '—' : `${r.roas.toFixed(2)}x`, getValue: (r) => r.roas },
  { key: 'cac', label: 'CAC', format: (r) => r.cac === null ? '—' : formatCurrency(r.cac, 'USD'), getValue: (r) => r.cac },
];

function sortRows(
  rows: readonly AnalyticsCampaignRow[],
  key: SortKey,
  dir: SortDir,
): AnalyticsCampaignRow[] {
  const col = COLUMNS.find((c) => c.key === key);
  if (col === undefined) return [...rows];
  return [...rows].sort((a, b) => compareNullable(col.getValue(a), col.getValue(b), dir));
}

export function CampaignsWidget(): JSX.Element {
  // State lives here (not inside CampaignsWidgetBody) so it's visible
  // above the ErrorBoundary — the boundary needs it to compute
  // `resetKey`, and CampaignsWidgetBody needs it as a fetch dep. Same
  // value on both sides.
  const [selectedWindow, setSelectedWindow] = useState<FunnelWindow>('7d');

  return (
    <AnalyticsV2ErrorBoundary title="Campaigns" resetKey={selectedWindow}>
      <CampaignsWidgetBody
        selectedWindow={selectedWindow}
        setSelectedWindow={setSelectedWindow}
      />
    </AnalyticsV2ErrorBoundary>
  );
}

interface CampaignsWidgetBodyProps {
  selectedWindow: FunnelWindow;
  setSelectedWindow: (w: FunnelWindow) => void;
}

function CampaignsWidgetBody({
  selectedWindow,
  setSelectedWindow,
}: CampaignsWidgetBodyProps): JSX.Element {
  const { sortKey, sortDir, handleSortClick } = useSortState<SortKey>('spend', 'desc');

  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsCampaigns({ window: selectedWindow }),
    [selectedWindow],
  );

  const sortedRows = useMemo(() => {
    if (data === null) return [];
    return sortRows(data.rows, sortKey, sortDir);
  }, [data, sortKey, sortDir]);

  const degraded = data === null ? [] : degradedSourceLabels(data.sourceStatuses);

  return (
    <AnalyticsV2Widget
      title="Campaigns"
      description={DESCRIPTION}
      selectedWindow={selectedWindow}
      onWindowChange={setSelectedWindow}
      data={data}
      error={error}
      isLoading={isLoading}
      degradedSources={degraded}
    >
      {data !== null && (
        sortedRows.length === 0 ? (
          // Distinguish "truly empty" from "empty because a source is
          // degraded" (CORE-959 review finding). The shell renders a
          // separate degradation banner above naming the failing sources.
          degraded.length > 0 ? (
            <p className="empty-state" role="status">
              No campaign data — some sources unavailable (see banner above).
            </p>
          ) : (
            <p className="empty-state" role="status">No campaigns for this window.</p>
          )
        ) : (
          <div className="table-wrap">
            <table className="campaigns-widget-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  {COLUMNS.map((col) => {
                    const isActive = sortKey === col.key;
                    const arrow = isActive ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';
                    return (
                      <th
                        key={col.key}
                        aria-sort={isActive ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                      >
                        <button
                          type="button"
                          className="campaigns-widget-sort-btn"
                          onClick={() => handleSortClick(col.key)}
                        >
                          {col.label}
                          {arrow}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  // Key by campaignName (guaranteed unique per backend
                  // contract — backend dedupes by name) instead of
                  // campaignId to avoid the rare Meta-numeric-vs-utm-string
                  // collision flagged in the CORE-959 review.
                  <tr key={row.campaignName}>
                    <td>{row.campaignName}</td>
                    {COLUMNS.map((col) => (
                      <td key={col.key} className="campaigns-widget-num">
                        {col.format(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AnalyticsV2Widget>
  );
}
